import { memo, useEffect, useState } from "react";
import {
  Clock3,
  ChevronLeft,
  ChevronRight,
  Copy,
  Info,
  LoaderCircle,
  Pencil,
  Play,
  RefreshCw,
  ShieldAlert,
  Trash2,
  UserRound,
} from "lucide-react";
import { messageDisplaySummary, modelSupportsVision } from "@lumen/shared";
import type {
  AgentDefinition,
  AgentWorkspaceApproval,
  Attachment,
  MessageActivityEvent,
  Model,
  SubagentRunView,
  User,
  ThinkingLevel,
} from "@lumen/shared";
import { t } from "../../i18n";
import {
  Composer,
  type ComposerActivity,
  type ComposerOptions,
} from "../composer/Composer";
import { Markdown } from "../Markdown";
import { ContextCompactionCard } from "./ContextCompactionCard";
import { MessageUsageDetails } from "./MessageUsageDetails";
import { AssistantMessageActivity } from "./AssistantMessageActivity";
import { MessageAttachments } from "./MessageAttachments";
import type { DisplayMessage } from "../../pages/chat/ChatPage";

function queueReasonLabel(reason: string) {
  switch (reason) {
    case "conversation-order":
      return "Earlier turn in this conversation is still active";
    case "capacity":
      return "Waiting for generation capacity";
    case "retry-backoff":
      return "Waiting for the next recovery attempt";
    case "waiting-for-worker":
      return "Waiting for a generation worker";
    default:
      return reason;
  }
}

export const MessageCard = memo(function MessageCard({
  message,
  model,
  repositoryId,
  agentName,
  agents,
  user,
  busy,
  decidingApprovalIds,
  approvals,
  onApproval,
  onQuestionAnswer,
  onEdit,
  onRegenerate,
  onContinue,
  onSubagentContinue,
  onDelete,
  onSelectVersion,
  activityModels,
  readOnly = false,
  editing: editingProp,
  onEditingChange,
}: {
  message: DisplayMessage;
  model?: Model;
  repositoryId?: string | null;
  agentName?: string;
  agents: AgentDefinition[];
  user: Pick<User, "name" | "avatarUrl">;
  busy: boolean;
  decidingApprovalIds: Set<string>;
  approvals: Map<string, AgentWorkspaceApproval>;
  onApproval?: (
    id: string,
    decision: "approved" | "denied",
    message?: string,
  ) => void;
  onQuestionAnswer?: (
    message: DisplayMessage,
    questionId: string,
    answers: string[],
    custom: boolean,
  ) => void | Promise<void>;
  onEdit?: (
    message: DisplayMessage,
    content: string,
    attachments: Attachment[],
    options: ComposerOptions,
  ) => Promise<void>;
  onRegenerate?: (message: DisplayMessage) => void;
  onContinue?: (message: DisplayMessage) => void;
  onSubagentContinue?: (run: SubagentRunView) => void;
  onDelete?: (message: DisplayMessage) => void;
  onSelectVersion?: (message: DisplayMessage, versionId: string) => void;
  activityModels?: Record<Exclude<ComposerActivity, "ask">, Model[]>;
  readOnly?: boolean;
  /** Controlled editing state, lifted so the page can hide the composer and later messages. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}) {
  const assistant = message.role === "assistant";
  const hasActivityEvents =
    Boolean(message.activityEvents?.length) ||
    (assistant &&
      (!message.generationStatus || message.generationStatus === "completed"));
  const customModel =
    message.customModel === true || Boolean(model?.remoteMetadata?.customModel);
  const messageModelName =
    message.modelName || message.modelRemoteId || model?.name || null;
  const persistedFailureCategory = message.error
    ? /timeout/i.test(message.error)
      ? "timeout"
      : /checkpoint|persistence/i.test(message.error)
        ? "checkpoint"
        : /capacity|concurrency/i.test(message.error)
          ? "capacity"
          : /worker|attempt/i.test(message.error)
            ? "worker"
            : /provider/i.test(message.error)
              ? "provider"
              : "unknown"
    : undefined;
  const statusDetails =
    message.generationStatusDetails ??
    (assistant && message.generationStatus === "failed" && message.error
      ? {
          phase: "failed" as const,
          jobStatus: "failed",
          failureCategory: persistedFailureCategory as
            | "capacity"
            | "checkpoint"
            | "timeout"
            | "worker"
            | "provider"
            | "unknown",
          failureReason: message.error,
        }
      : undefined);
  const statusLabel =
    statusDetails?.phase === "queued"
      ? "Queued"
      : statusDetails?.phase === "retrying"
        ? "Waiting for capacity"
        : statusDetails?.phase === "running"
          ? "Generating"
          : statusDetails?.phase === "waiting"
            ? statusDetails.jobStatus === "awaiting-approval"
              ? "Waiting for approval"
              : statusDetails.jobStatus === "awaiting-question"
                ? "Waiting for your answer"
                : "Waiting for input"
            : statusDetails?.phase === "failed"
              ? "Generation failed"
              : statusDetails?.phase === "degraded"
                ? "Live updates degraded"
                : null;
  const usageEvents = message.usageEvents ?? [];
  const compaction = message.activityEvents?.find(
    (event): event is Extract<MessageActivityEvent, { type: "compaction" }> =>
      event.type === "compaction",
  );
  const editable = !readOnly && (message.role === "user" || assistant);
  const [editingState, setEditingState] = useState(false);
  const editing = editingProp ?? editingState;
  const setEditing = (next: boolean) => {
    setEditingState(next);
    onEditingChange?.(next);
  };
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);
  const [editAgentId, setEditAgentId] = useState(message.agentId ?? "");
  const [editThinkingLevel, setEditThinkingLevel] = useState<
    ThinkingLevel | ""
  >("");
  const [editEnabledActivities, setEditEnabledActivities] = useState<
    ComposerActivity[]
  >(() => ["ask", ...(message.generationParameters?.enabledActivities ?? [])]);
  const [editActivityModelIds, setEditActivityModelIds] = useState(
    () => message.generationParameters?.activityModelIds ?? {},
  );
  const [editActivitySettings, setEditActivitySettings] = useState(
    () => message.generationParameters?.activitySettings ?? {},
  );
  useEffect(() => {
    if (!editing) setEditAgentId(message.agentId ?? "");
  }, [editing, message.agentId]);
  useEffect(() => {
    if (!editing) {
      setEditEnabledActivities([
        "ask",
        ...(message.generationParameters?.enabledActivities ?? []),
      ]);
      setEditActivityModelIds(
        message.generationParameters?.activityModelIds ?? {},
      );
      setEditActivitySettings(
        message.generationParameters?.activitySettings ?? {},
      );
    }
  }, [editing, message.id, message.generationParameters]);
  if (message.role === "system" && compaction)
    // Standalone fallback: the checkpoint's parent fragment is outside the
    // loaded window, so the folded inline rendering is unavailable. Keep the
    // full-width placement the inline card normally inherits from its parent
    // message card.
    return (
      <div
        data-chat-message={message.id}
        className="mx-auto w-full max-w-3xl px-4 py-3"
      >
        <ContextCompactionCard
          event={compaction}
          messageContent={message.content}
        />
      </div>
    );
  return (
    <article
      data-chat-message={message.id}
      className={`chat-message group mx-auto flex w-full max-w-3xl min-w-0 py-6 ${editing ? "gap-0 px-0 sm:gap-4 sm:px-4" : "gap-4 px-4"}`}
    >
      <div
        className={`${editing ? "hidden" : "grid"} size-8 shrink-0 place-items-center overflow-hidden rounded-xl ${assistant ? "bg-lumen-600 text-white" : "bg-stone-200 text-stone-600 dark:bg-white/10 dark:text-stone-200"}`}
      >
        {assistant ? (
          model?.icon?.startsWith("/") ||
          model?.icon?.startsWith("http") ||
          model?.icon?.startsWith("data:image/") ? (
            <img src={model.icon} alt="" className="size-full object-cover" />
          ) : (
            model?.icon || "✦"
          )
        ) : user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={`${user.name}'s avatar`}
            className="size-full object-cover"
          />
        ) : (
          <UserRound size={16} />
        )}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div
          className={`mb-1 flex flex-wrap items-center gap-2 text-xs font-bold tracking-wider text-stone-400 uppercase ${editing ? "hidden" : ""}`}
        >
          {!assistant && <span>{t("You")}</span>}
          {assistant &&
            messageModelName &&
            (customModel ? (
              <span
                data-message-model={messageModelName}
                title={
                  message.modelRemoteId
                    ? t("Model: {{model}}", { model: message.modelRemoteId })
                    : undefined
                }
                aria-label={t("Model: {{model}}", { model: messageModelName })}
                className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold tracking-normal text-stone-600 normal-case dark:bg-white/10 dark:text-stone-300"
              >
                {t("Model: {{model}}", { model: messageModelName })}
              </span>
            ) : (
              <span data-message-model={messageModelName}>
                {messageModelName}
              </span>
            ))}
          {agentName && (
            <span className="rounded-full bg-lumen-50 px-2 py-0.5 text-[10px] text-lumen-700 normal-case dark:bg-lumen-900/30 dark:text-lumen-300">
              {agentName}
            </span>
          )}
          {!assistant && Boolean(message.skillIds?.length) && (
            <span className="rounded-full bg-lumen-50 px-2 py-0.5 text-[10px] text-lumen-700 normal-case dark:bg-lumen-900/30 dark:text-lumen-300">
              {t("Skill")}
            </span>
          )}
          {!assistant && Boolean(message.toolIds?.length) && (
            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700 normal-case dark:bg-violet-950/40 dark:text-violet-300">
              {t("tools", { count: message.toolIds!.length })}
            </span>
          )}
          {!assistant && Boolean(message.mcpServerIds?.length) && (
            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700 normal-case dark:bg-violet-950/40 dark:text-violet-300">
              {message.mcpServerIds!.length} MCP
            </span>
          )}
          {!assistant && message.agentWorkspaceId && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700 normal-case dark:bg-amber-950/40 dark:text-amber-300">
              {t("Agent workspace")}
            </span>
          )}
        </div>
        {assistant && !editing && (
          <AssistantMessageActivity
            message={message}
            busy={busy}
            decidingApprovalIds={decidingApprovalIds}
            approvals={approvals}
            onApproval={readOnly ? undefined : onApproval}
            onQuestionAnswer={
              readOnly
                ? undefined
                : (questionId, answers, custom) =>
                    onQuestionAnswer?.(message, questionId, answers, custom)
            }
            onContinue={readOnly ? undefined : () => onContinue?.(message)}
            onSubagentContinue={readOnly ? undefined : onSubagentContinue}
          />
        )}
        {editing ? (
          <Composer
            mode="edit"
            initialValue={message.content}
            initialAttachments={message.attachments}
            initialOptions={{
              toolIds: message.toolIds ?? [],
              artifactIds: message.artifactIds ?? [],
              skillIds: message.skillIds ?? [],
              mcpServerIds: message.mcpServerIds ?? [],
              mcpToolSelections: message.mcpToolSelections ?? {},
              agentWorkspaceId:
                message.agentWorkspaceIds?.[0] ??
                message.agentWorkspaceId ??
                null,
              agentWorkspaceIds: (() => {
                if (message.agentWorkspaceIds?.length)
                  return message.agentWorkspaceIds;
                if (message.agentWorkspaceId) return [message.agentWorkspaceId];
                return [];
              })(),
              repositoryIds: message.repositoryIds ?? {},
              repositoryId:
                (message.agentWorkspaceIds?.[0] &&
                  message.repositoryIds?.[message.agentWorkspaceIds[0]]?.[0]) ??
                (message.agentWorkspaceId &&
                  message.repositoryIds?.[message.agentWorkspaceId]?.[0]) ??
                repositoryId ??
                null,
              agentId: message.agentId ?? null,
              generationParameters: message.generationParameters ?? {},
            }}
            activity={message.generationParameters?.activity ?? "ask"}
            onSend={async (content, attachments, options) => {
              // Keep the edit composer mounted while saving so it does not
              // flicker back to the card body or reveal the send composer.
              try {
                await onEdit?.(message, content, attachments, options);
              } catch {
                return;
              }
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            disabled={busy}
            agents={agents}
            agentId={editAgentId}
            onAgentChange={setEditAgentId}
            supportsVision={model ? modelSupportsVision(model) : false}
            supportedParameters={model?.supportedParameters ?? []}
            supportsTools={Boolean(
              model?.capabilities.tools && model.toolsEnabled,
            )}
            thinkingLevels={model?.thinkingLevels}
            supportsThinking={model?.supportedParameters.some(
              (parameter) =>
                parameter === "reasoning" || parameter === "reasoning_effort",
            )}
            activityModels={activityModels}
            activityModelIds={editActivityModelIds}
            enabledActivities={editEnabledActivities}
            activitySettings={editActivitySettings}
            onActivityModelChange={(activity, modelId) => {
              setEditActivityModelIds((current) => ({
                ...current,
                [activity]: modelId,
              }));
            }}
            onActivitySettingsChange={(activity, settings) => {
              const nextSettings = settings ?? {};
              setEditActivitySettings((current) => ({
                ...current,
                [activity]: nextSettings,
              }));
              if (nextSettings.modelId) {
                setEditActivityModelIds((current) => ({
                  ...current,
                  [activity]: nextSettings.modelId,
                }));
              }
            }}
            onToggleActivity={(activity) => {
              setEditEnabledActivities((current) =>
                current.includes(activity)
                  ? current.filter((item) => item !== activity)
                  : [...current, activity],
              );
            }}
            thinkingLevel={editThinkingLevel}
            onThinkingLevelChange={setEditThinkingLevel}
          />
        ) : !hasActivityEvents && message.content ? (
          <Markdown
            streaming={busy}
            mentionSelection={
              message.role === "user"
                ? {
                    workspaceId:
                      message.agentWorkspaceIds?.[0] ??
                      message.agentWorkspaceId,
                    repositoryId:
                      message.repositoryIds?.[
                        message.agentWorkspaceIds?.[0] ??
                          message.agentWorkspaceId ??
                          ""
                      ]?.[0] ?? repositoryId,
                  }
                : undefined
            }
          >
            {message.content}
          </Markdown>
        ) : !hasActivityEvents &&
          !message.error &&
          message.toolEvents?.some(
            (event) => event.status === "awaiting-approval",
          ) ? (
          <span className="inline-flex items-center gap-2 text-sm text-amber-600">
            <ShieldAlert size={15} />
            {t("Waiting for command approval…")}
          </span>
        ) : !hasActivityEvents &&
          !message.error &&
          message.generationStatus === "awaiting-question" ? (
          <span className="inline-flex items-center gap-2 text-sm text-amber-600">
            <ShieldAlert size={15} />
            {t("Waiting for your answer…")}
          </span>
        ) : !message.error && message.generationStatus === "running" ? (
          <span className="inline-flex items-center gap-2 text-sm text-stone-400">
            {statusDetails?.jobStatus === "queued" ? (
              <Clock3 size={15} />
            ) : (
              <LoaderCircle size={15} className="animate-spin" />
            )}
            {t(
              statusDetails?.jobStatus === "queued"
                ? "Queued"
                : "Starting response…",
            )}
          </span>
        ) : null}
        {assistant &&
          !editing &&
          statusInfoOpen &&
          (statusDetails || usageEvents.length > 0) && (
            <div
              className={`mt-3 rounded-xl border p-3 text-xs ${statusDetails?.degraded || statusDetails?.phase === "failed" ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200" : "border-stone-200 bg-stone-50 text-stone-600 dark:border-white/10 dark:bg-white/5 dark:text-stone-300"}`}
              data-generation-status={statusDetails?.phase}
            >
              {statusDetails && statusLabel && (
                <>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                    <span>{statusLabel}</span>
                    {statusDetails.attempt !== undefined &&
                      statusDetails.attempt > 0 && (
                        <span>· attempt {statusDetails.attempt}</span>
                      )}
                    {statusDetails.retryAttempt !== undefined &&
                      statusDetails.retryAttempt > 0 && (
                        <span>· retry {statusDetails.retryAttempt}</span>
                      )}
                    {statusDetails.capacityRetryCount !== undefined &&
                      statusDetails.capacityRetryCount > 0 && (
                        <span>
                          · capacity retry {statusDetails.capacityRetryCount}
                        </span>
                      )}
                  </div>
                  {statusDetails.queuePosition !== undefined && (
                    <div className="mt-1">
                      Queue position: {statusDetails.queuePosition}
                    </div>
                  )}
                  {statusDetails.queueReason && (
                    <div className="mt-1">
                      Queue reason:{" "}
                      {queueReasonLabel(statusDetails.queueReason)}
                    </div>
                  )}
                  {statusDetails.nextRetryAt && (
                    <div className="mt-1">
                      Next retry:{" "}
                      {new Date(statusDetails.nextRetryAt).toLocaleTimeString()}
                    </div>
                  )}
                  {statusDetails.recoveryState &&
                    statusDetails.recoveryState !== "active" &&
                    statusDetails.recoveryState !== "terminal" && (
                      <div className="mt-1">
                        Recovery state: {statusDetails.recoveryState}
                      </div>
                    )}
                  {statusDetails.workerState &&
                    statusDetails.workerState !== "terminal" && (
                      <div className="mt-1">
                        Worker state: {statusDetails.workerState}
                      </div>
                    )}
                  {statusDetails.checkpointed &&
                    statusDetails.phase !== "failed" &&
                    statusDetails.phase !== "completed" && (
                      <div className="mt-1">
                        Checkpoint saved; recovery can resume.
                      </div>
                    )}
                  {statusDetails.failureCategory && (
                    <div className="mt-1">
                      Reason:{" "}
                      {statusDetails.failureReason ??
                        statusDetails.failureCategory}
                    </div>
                  )}
                  {statusDetails.correlationId && (
                    <div className="mt-1 text-[11px] opacity-75">
                      Reference: {statusDetails.correlationId.slice(0, 8)}
                    </div>
                  )}
                </>
              )}
              {usageEvents.length > 0 && (
                <MessageUsageDetails events={usageEvents} />
              )}
            </div>
          )}
        {!editing &&
          message.error &&
          !message.activityEvents?.some((event) => event.type === "error") && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <strong>{t("Provider error")}</strong>
              <br />
              {message.error}
            </div>
          )}
        {!editing && <MessageAttachments message={message} />}
        {!editing && !message.id.startsWith("optimistic-") && (
          <div className="mt-3 flex min-h-8 flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              {!readOnly &&
                (message.versionCount ?? message.versions?.length ?? 1) > 1 &&
                (() => {
                  const versions = [...(message.versions ?? [])].sort(
                    (a, b) => a.versionIndex - b.versionIndex,
                  );
                  const index = Math.max(
                    0,
                    versions.findIndex((version) => version.id === message.id),
                  );
                  return (
                    <div
                      className="flex items-center rounded-lg border border-stone-200 bg-white/60 dark:border-white/10 dark:bg-white/5"
                      aria-label={`Version ${index + 1} of ${versions.length}`}
                    >
                      <button
                        type="button"
                        disabled={busy || index <= 0}
                        onClick={() =>
                          onSelectVersion?.(message, versions[index - 1].id)
                        }
                        className="p-1.5 text-stone-500 disabled:opacity-30"
                        title={t("Previous version")}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <span className="min-w-10 text-center text-[11px] font-semibold text-stone-500">
                        {index + 1} / {versions.length}
                      </span>
                      <button
                        type="button"
                        disabled={busy || index >= versions.length - 1}
                        onClick={() =>
                          onSelectVersion?.(message, versions[index + 1].id)
                        }
                        className="p-1.5 text-stone-500 disabled:opacity-30"
                        title={t("Next version")}
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  );
                })()}
            </div>
            <div className="flex items-center gap-1">
              {!readOnly &&
                assistant &&
                (statusDetails
                  ? statusLabel !== null
                  : usageEvents.length > 0) && (
                  <button
                    type="button"
                    onClick={() => setStatusInfoOpen((open) => !open)}
                    className={`button p-2 ${statusInfoOpen ? "text-lumen-700 dark:text-lumen-300" : "text-stone-400"}`}
                    title={t("Generation details")}
                    aria-label={t("Generation details")}
                    aria-expanded={statusInfoOpen}
                    aria-pressed={statusInfoOpen}
                  >
                    <Info size={14} />
                  </button>
                )}
              {!readOnly && assistant && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRegenerate?.(message)}
                  className="button gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-lumen-700 dark:text-lumen-300"
                  title={t("Create another response with the selected model")}
                >
                  <RefreshCw size={14} /> {t("Regenerate")}
                </button>
              )}
              {!readOnly &&
                assistant &&
                message.generationStatus !== "running" &&
                message.generationStatus !== "awaiting-approval" &&
                message.generationStatus !== "awaiting-question" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onContinue?.(message)}
                    className="button gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-lumen-700 dark:text-lumen-300"
                    title={t("Continue this response with more detail")}
                  >
                    <Play size={14} /> {t("Continue")}
                  </button>
                )}
              <div className="flex gap-1 opacity-60 transition group-focus-within:opacity-100 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() =>
                    void navigator.clipboard.writeText(
                      assistant && message.generationStatus === "completed"
                        ? messageDisplaySummary(message).finalContent
                        : message.content,
                    )
                  }
                  className="button p-1.5 text-stone-400"
                  title={t("Copy")}
                >
                  <Copy size={14} />
                </button>
                {editable && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(true)}
                    className="button p-1.5 text-stone-400"
                    title={
                      assistant
                        ? t("Edit response as a new version")
                        : t("Edit prompt as a new version")
                    }
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDelete?.(message)}
                    className="button p-1.5 text-stone-400 hover:text-red-600"
                    title={t("Delete this and later messages")}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
});
